import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'formly-field-input',
  template: `
    <label *ngIf="!props.label" [for]="elementId" class="wcag-visuallyhidden">
      <ng-container *ngIf="props.placeholder; else EMPTY_LABEL">{{ props.placeholder }}</ng-container>
      <ng-template #EMPTY_LABEL>Input field</ng-template>
    </label>
    <input
      *ngIf="props.type !== 'number' && props.type !== 'formatted-number' && props.type !== 'integer-number'; else numberTmp"
      #inputEl
      [class.is-invalid]="showError"
      [disable-with-validation]="disabledField"
      [disabled]="isFormDisabled"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [id]="elementId"
      [tabindex]="tabIndex"
      type="text"
      (change)="onChange($event)"
      class="form-control"
      [attr.aria-labelledby]="elementId"
    />
    <ng-template #numberTmp>
      <ng-container *ngIf="props.type == 'formatted-number'">
        <input
          #inputEl
          [allowNegativeNumbers]="props.allowNegativeNumbers"
          [class.is-invalid]="showError"
          [disable-with-validation]="disabledField"
          [disabled]="isFormDisabled"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [maxlength]="!props.skipLengthValidation ? 19 : 100"
          [id]="elementId"
          [mask]="formattedNumberMask"
          [thousandSeparator]="commaSeparator"
          [decimalMarker]="'.'"
          [tabindex]="tabIndex"
          (change)="onChange($event)"
          class="form-control"
          type="text"
        />
      </ng-container>

      <ng-container *ngIf="props.type == 'integer-number'">
        <input
          #inputEl
          step="1"
          [class.hide-arrows]="hideArrows"
          [class.is-invalid]="showError"
          [disable-with-validation]="disabledField"
          [disabled]="isFormDisabled"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [id]="elementId"
          [tabindex]="tabIndex"
          (change)="onChange($event)"
          (keypress)="forceInteger($event)"
          (paste)="prevent($event)"
          (wheel)="handleWheel($event, props.min, props.max)"
          class="form-control"
          type="number"
        />
      </ng-container>

      <ng-container *ngIf="props.type == 'number'">
        <input
          #inputEl
          [class.hide-arrows]="hideArrows"
          [class.is-invalid]="showError"
          [disable-with-validation]="disabledField"
          [disabled]="isFormDisabled"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [id]="elementId"
          [tabindex]="tabIndex"
          (change)="onChange($event)"
          (wheel)="prevent($event)"
          class="form-control"
          type="number"
        />
      </ng-container>
    </ng-template>
  `,
})
export class FormlyFieldInputExtraComponent extends FieldType implements OnInit, OnDestroy {
  disabledField = false;
  localFormDisabled = false;
  commaSeparator;

  private _destroy$ = new Subject();

  @ViewChild('inputEl', {static: false}) inputEl: ElementRef;

  constructor(private translate: TranslateService, private cdr: ChangeDetectorRef) {
    super();
  }

  // get commaSeparator() {
  //   return this.translate.currentLang === 'en' ? ',' : ' ';
  // }

  get formattedNumberMask(): string {
    return this.props.mask || 'separator.0';
  }

  ngOnInit() {
    this.commaSeparator = this.translate.currentLang === 'en' ? ',' : ' ';
    this.localFormDisabled = this.props.formDisabled || this.props.disabled;
    if (this.form.enabled) {
      const formControlDisableFn = this.formControl.disable;
      const formControlEnableFn = this.formControl.enable;
      this.formControl.disable = opts => {
        if (this.props.ignoreLocalFormDisabled) {
          formControlDisableFn.apply(this.formControl, arguments);
        }
        this.disabledField = true;
      };

      this.formControl.enable = opts => {
        if (this.props.ignoreLocalFormDisabled) {
          formControlEnableFn.apply(this.formControl, arguments);
        }
        this.disabledField = false;
      };

      this.disabledField = this.isFormDisabled();
    } else {
      this.disabledField = true;
    }

    this.initDefaultValueSub();

    this.handleLanguageChange();
  }

  private handleLanguageChange() {
    this.translate.onLangChange.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.commaSeparator = this.translate.currentLang === 'en' ? ',' : ' ';
      if (this.props.type === 'formatted-number') {
        const currentValue = this.formControl.value;
        if (currentValue !== null && currentValue !== undefined) {
          const cleanValue = String(currentValue).replace(/,|\s/g, '');
          setTimeout(() => {
            this.formControl.setValue(cleanValue);
          });
        }
      }
    });
  }

  isFormDisabled(): boolean {
    return this.form.disabled === true || this.localFormDisabled;
  }

  get hideArrows(): boolean {
    return this.props.hideArrows;
  }

  get elementId(): string {
    if (this.field.id && this.field.id.includes('formly_')) {
      let prefix = this.form['id'];

      // check if in repeat section
      if (!prefix && this.field.parent.parent && this.field.parent.parent.parent && this.field.parent.parent.parent.type === 'repeat') {
        const cnt = this.field.parent.parent.key;
        prefix = `${this.field.parent.parent.parent['id']}_${cnt}`;
      }
      if (!prefix && this.field.parent.parent && this.field.parent.parent.type === 'form-array') {
        prefix = this.field.parent['id'];
      }

      return `${prefix ? prefix + '_' : ''}${(this.key as string).replace('.', '_')}`;
    }
    return this.field.id;
  }

  onChange($event: Event): void {
    if (typeof this.formControl.value === 'string') {
      setTimeout(() => {
          this.formControl.setValue(this.formControl.value.trim());
      });
    }

    if (this.props.syncFromModel && this.model[this.key as string]) {
      this.inputEl.nativeElement.value = this.model[this.key as string];
    }
    if (this.props.allowNegativeNumbers === false) {
      this.formControl.setValue(Math.abs(this.formControl.value));
    }
    $event.preventDefault();
    if (this.props.onChange) {
      this.props.onChange($event, this.form);
    }
  }

  public forceInteger(event) {
    if (event.key !== 'Enter' && !/[0-9]/.test(event.key)) {
      this.prevent(event);
    }
  }

  public prevent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  public handleWheel(event, min = null, max = null) {
    if (min && min > event.target.valueAsNumber) {
      this.prevent(event);
    }

    if (max && max < event.target.valueAsNumber) {
      this.prevent(event);
    }
  }

  private initDefaultValueSub(): void {
    const { defaultValue } = this.props;

    if (typeof defaultValue !== 'string' && typeof defaultValue !== 'number') {
      return;
    }

    const value = typeof defaultValue === 'number' ? defaultValue.toString() : defaultValue;

    this.formControl.setValue(value);
  }

  public get tabIndex(): number {
    return this.props.tabindex || (this.disabledField || this.field.hide) ? -1 : 0;
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }
}
